import { type AnyGraphNode, NodeTypeId, nodeName } from '../types/graphNodes';
import type { GraphDomain } from '../types/graph.types';
import { inAppDomain } from '../app/appPolicy';
import { compareDisplayNames, isMissingName } from './resultOrdering';
import type { BrowseFacet } from './facets';

export interface FacetValue {
  nodeId: number;
  name: string;
  bhajanCount: number;
}

/** All values of a facet (deity/ragam/talam/composer) with bhajan counts, sorted by name. */
export function deriveFacetValues(
  nodes: AnyGraphNode[],
  facet: BrowseFacet,
  domain?: GraphDomain
): FacetValue[] {
  const counts = new Map<number, number>();
  for (const n of nodes) {
    if (n.nodeTypeId !== NodeTypeId.BHAJAN || !inAppDomain(n, domain)) continue;
    for (const e of n.edges ?? []) {
      if (e.name === facet.edgeName) counts.set(e.nodeId, (counts.get(e.nodeId) ?? 0) + 1);
    }
  }
  const values: FacetValue[] = [];
  for (const n of nodes) {
    if (n.nodeTypeId !== facet.nodeTypeId) continue;
    const name = nodeName(n);
    // A facet value is a browse *category*, not an item, so a placeholder
    // ('.', 'missing', or any name with no letters/digits) is excluded
    // outright — it is not something anyone can browse to. This is the
    // opposite of a nameless *result*, which is a real item and is merely
    // sunk to the bottom by compareDisplayNames. Mirrors jnana-native's
    // onFetchRagams, which filters the same markers from browse-by-ragam.
    //
    // Checked explicitly rather than leaning on nodeName() returning
    // undefined for '.': nodeName only strips '' and '.', so 'missing' and
    // letterless names would otherwise leak in, and a change to nodeName's
    // placeholder rules must not silently change what browse lists show.
    if (!name || isMissingName(name)) continue;
    // Skip values no bhajan references — a browse entry that leads to an
    // empty list is useless (e.g. a ragam no in-domain bhajan is set in).
    // Counts come only from the edges of in-domain bhajans, so a shared
    // reference node this app's bhajans never point at is dropped here.
    const bhajanCount = counts.get(n.nodeId) ?? 0;
    if (bhajanCount === 0) continue;
    values.push({ nodeId: n.nodeId, name, bhajanCount });
  }
  values.sort((a, b) => compareDisplayNames(a.name, b.name) || a.nodeId - b.nodeId);
  return values;
}
